import { BreakpointObserver } from '@angular/cdk/layout'
import { CdkScrollableModule } from '@angular/cdk/scrolling'
import { AsyncPipe, CommonModule } from '@angular/common'
import {
  afterRenderEffect,
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  ElementRef,
  inject,
  input,
  signal,
  Signal,
  TemplateRef,
  viewChild,
} from '@angular/core'
import { toSignal } from '@angular/core/rxjs-interop'
import { MatButtonModule } from '@angular/material/button'
import { MatIconModule } from '@angular/material/icon'
import { MatListModule } from '@angular/material/list'
import { MatSidenavContainer, MatSidenavModule } from '@angular/material/sidenav'
import { MatToolbarModule } from '@angular/material/toolbar'
import { MatTooltipModule } from '@angular/material/tooltip'
import { ActivatedRoute, RouterModule } from '@angular/router'
import { Observable } from 'rxjs'
import { map, shareReplay } from 'rxjs/operators'
import { SidesheetService, StageService, ToolbarService } from '../../services'
import { ProfileWidgetComponent, ProfilWidgetDaten } from '../auth/profile-widget/profile-widget.component'
import { ConnectionBannerComponent } from '../connection-banner/connection-banner.component'
import { ErrorCenterButtonComponent } from '../error-center/error-center-button/error-center-button.component'
import { StageBarComponent } from '../stage-bar/stage-bar.component'
import { NavActionListComponent } from './nav-action-list/nav-action-list.component'
import { NavigationButtonComponent } from './navigation-button/navigation-button.component'

export interface NavigationItem {
  label: string
  route: string
  queryParams?: { [key: string]: string }
  icon: string // mat-icon name
  badge?: number | string
}

export interface NavigationSection {
  label: string
  items: NavigationItem[]
}

export interface NavigationGroup {
  items: NavigationItem[]
  sections?: NavigationSection[]
}

/**
 * Layout der Seitenleiste.
 * - `rail`: schmale Leiste, Icon über zentriertem Label. Gut bis ~7 Ziele (Selfservice).
 * - `list`: breite Liste, Icon + linksbündiges Label + Badge rechts. Für Apps mit vielen Zielen (internal).
 * - `responsive`: richtet sich nach dem Fenster (Leistungserbringer-Portal, Abstimmung Tim, Max und Onno
 *   2026-10-04, Canvas „Seitenleiste für Laptop und Tablet“): auf grossen Bildschirmen die Arbeitsliste
 *   mit Gruppen ({@link FENSTER_ARBEITSLISTE}), sonst die Leiste wie gehabt, und in sehr niedrigen
 *   Fenstern nur die Icons ({@link FENSTER_NUR_ICONS}). Breiter als heute wird die Leiste damit nur dort,
 *   wo Platz ist; der Wunsch nach der Liste scheiterte vorher genau daran („breiter würde ich nicht“).
 */
export type NavigationLayout = 'rail' | 'list' | 'responsive'

/**
 * Ab hier zeigt `responsive` die Arbeitsliste: 1600 px Breite (13"-Laptops wie das MacBook Air mit 1470 px
 * bleiben darunter) und 720 px Hoehe, die die Liste mit 44-px-Zeilen und zwei Gruppenkoepfen braucht.
 */
const FENSTER_ARBEITSLISTE = '(min-width: 100rem) and (min-height: 45rem)'

/**
 * Darunter blendet `responsive` die Beschriftungen aus und schmaelert die Leiste auf 80 px. Die kompakte
 * Stufe (unter 850 px, in den SCSS-Dateien) traegt mit acht Eintraegen bis 720 px Fensterhoehe, auch auf
 * env-test (Toolbar und Stage-Streifen abgezogen); darunter wuerde sie wieder scrollen.
 */
const FENSTER_NUR_ICONS = 'not (min-height: 45rem)'

const DEFAULT_HOME_ITEM: NavigationItem = { label: 'Startseite', icon: 'home', route: '/landing' }

@Component({
  selector: 'lib-navigation',
  templateUrl: './navigation.component.html',
  styleUrl: './navigation.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    '[class.layout-list]': "layoutEffektiv() === 'list'",
    '[class.layout-responsive]': "layout() === 'responsive'",
    '[class.nur-icons]': 'nurIcons()',
    '[class.mit-stage-bar]': 'stageService.showStageBanner()',
  },
  imports: [
    CommonModule,
    AsyncPipe,
    RouterModule,
    MatToolbarModule,
    MatSidenavModule,
    MatListModule,
    MatButtonModule,
    MatIconModule,
    MatTooltipModule,
    ProfileWidgetComponent,
    ErrorCenterButtonComponent,
    StageBarComponent,
    ConnectionBannerComponent,
    NavActionListComponent,
    CdkScrollableModule,
    NavigationButtonComponent,
  ],
})
export class NavigationComponent {
  readonly activatedRoute = inject(ActivatedRoute)
  /** Nur fuer die Zustandsklasse an `.main-content`: bei offenem Sheet wird der Inhalt schmaler. */
  protected readonly sidesheets = inject(SidesheetService)
  readonly toolbarService = inject(ToolbarService)
  /** Fuer die Host-Klasse `mit-stage-bar`: der Streifen ueber der Toolbar kostet Hoehe. */
  protected readonly stageService = inject(StageService)

  private readonly breakpointObserver = inject(BreakpointObserver)

  readonly fabTemplate = input<TemplateRef<unknown>>()

  readonly actionsTemplate = input<TemplateRef<unknown>>()

  /** Siehe {@link NavigationLayout}. Default `rail` — bestehende Apps bleiben unverändert. */
  readonly layout = input<NavigationLayout>('rail')

  /**
   * Ob der eingeloggte Nutzer SaniUp-Mitarbeiter:in ist. Schaltet die SaniUp-internen Werkzeuge in
   * der Toolbar frei — aktuell die Fehlerzentrale. Wer das ist, weiss nur die App (die interne App
   * setzt es fest, im Leistungserbringer-Portal entscheidet es die E-Mail-Domain).
   */
  readonly saniupEmployee = input(false)

  /**
   * Eigenes Logo oben links statt des SaniUp-Logos, als fertige Bild-URL (auch Object-URL).
   * `null` = SaniUp-Logo. Das Leistungserbringer-Portal setzt hier das Logo des Hauses, wenn ein
   * Callcenter-Agent angemeldet ist.
   */
  readonly logoUrl = input<string | null>(null)

  /** Name, Adresse und Bild der angemeldeten Person fuer den Profil-Knopf; ohne bleibt es beim Auth0-Bild. */
  readonly profil = input<ProfilWidgetDaten | null>(null)

  /**
   * Der fest oben stehende Startseiten-Eintrag. Überschreibbar, weil die Landing-URL je App
   * unterschiedlich ist (`/landing` vs. `/internal/landing`) — bei falscher Route greift
   * `routerLinkActive` nicht und der Eintrag wird nie als aktiv markiert.
   */
  readonly homeItem = input<NavigationItem>(DEFAULT_HOME_ITEM)

  // Data from Router
  readonly navigationGroups = signal<NavigationGroup>({ items: [] })
  // Data from Input
  readonly navGroups = input<NavigationGroup>()

  readonly _navGroups = computed(() => {
    const navGroups = this.navGroups()
    if (navGroups) {
      return navGroups
    }
    return this.navigationGroups()
  })

  readonly appTitle: Signal<string>

  readonly scrollContainer = viewChild<ElementRef<HTMLDivElement>>('scrollContainer')
  readonly scrollSentinel = viewChild<ElementRef<HTMLDivElement>>('scrollSentinel')
  readonly showScrollIndicator = signal(false)

  private readonly sidenavContainer = viewChild(MatSidenavContainer)

  constructor() {
    // Die Inhaltsflaeche rueckt nur von selbst nach, wenn die Leiste auf- oder zugeht, nicht wenn sie
    // beim Wechsel der Stufe (80 / 140 / 224 px) ihre Breite aendert. Ohne diesen Anstoss laege der
    // Inhalt nach einem Fensterwechsel unter der Leiste oder liesse eine Luecke stehen.
    afterRenderEffect(() => {
      this.layoutEffektiv()
      this.nurIcons()
      this.sidenavContainer()?.updateContentMargins()
    })

    this.navigationGroups.set((this.activatedRoute.snapshot.data['links'] ?? { items: [] }) as NavigationGroup)
    this.appTitle = toSignal(this.activatedRoute.data.pipe(map((d) => d['title'] as string)), { initialValue: 'Titel' })

    effect((onCleanup) => {
      const container = this.scrollContainer()?.nativeElement
      const sentinel = this.scrollSentinel()?.nativeElement
      if (!container || !sentinel) return

      const observer = new IntersectionObserver(([entry]) => this.showScrollIndicator.set(!entry.isIntersecting), {
        root: container,
        threshold: 0,
      })
      observer.observe(sentinel)
      onCleanup(() => observer.disconnect())
    })
  }

  readonly isHandset$: Observable<boolean> = this.breakpointObserver.observe('not (min-width: 64rem)').pipe(
    map((result) => result.matches),
    shareReplay(),
  )

  private readonly istHandset = toSignal(this.isHandset$, { initialValue: false })
  private readonly fensterArbeitsliste = toSignal(this.breakpointObserver.observe(FENSTER_ARBEITSLISTE).pipe(map((r) => r.matches)), {
    initialValue: false,
  })
  private readonly fensterNurIcons = toSignal(this.breakpointObserver.observe(FENSTER_NUR_ICONS).pipe(map((r) => r.matches)), {
    initialValue: false,
  })

  /** Das Layout, das gerade gezeichnet wird: `responsive` aufgeloest nach dem Fenster. */
  readonly layoutEffektiv = computed<'rail' | 'list'>(() => {
    const layout = this.layout()
    if (layout !== 'responsive') {
      return layout
    }
    return this.fensterArbeitsliste() ? 'list' : 'rail'
  })

  /**
   * Nur Icons, ohne Beschriftung (Tooltip statt Label). Nicht im Handset-Modus: dort faehrt die Leiste
   * als Overlay heraus und hat ohnehin Platz fuer die Beschriftung.
   */
  readonly nurIcons = computed(
    () => this.layout() === 'responsive' && this.layoutEffektiv() === 'rail' && this.fensterNurIcons() && !this.istHandset(),
  )

  /**
   * Die Eintraege ohne Gruppen. Gruppenkoepfe zeichnet nur die Liste; in der Leiste kosteten sie je
   * Gruppe rund 36 px Hoehe, und genau die fehlen ihr auf einem 13"-Laptop.
   */
  readonly flacheEintraege = computed(() => {
    const gruppen = this._navGroups()
    return gruppen.sections ? gruppen.sections.flatMap((section) => section.items) : gruppen.items
  })
}
